/** Display metadata for the Notification Center. Mirrors backend services/notifications/taxonomy.js. */

import {
  BadgePercent,
  Megaphone,
  MessageSquare,
  Repeat,
  ShoppingCart,
  Sparkles,
  UserRound,
  type LucideIcon,
} from 'lucide-react'
import type { CampaignStatus, Priority } from './types'

export const STATUS_TONE: Record<CampaignStatus, 'neutral' | 'success' | 'warning' | 'info' | 'gold'> = {
  draft: 'neutral',
  review: 'warning',
  approved: 'info',
  scheduled: 'info',
  sending: 'gold',
  sent: 'success',
  paused: 'warning',
  cancelled: 'neutral',
  failed: 'warning',
}

export const STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: 'Draft',
  review: 'In review',
  approved: 'Approved',
  scheduled: 'Scheduled',
  sending: 'Sending',
  sent: 'Sent',
  paused: 'Paused',
  cancelled: 'Cancelled',
  failed: 'Failed',
}

export const PRIORITY_LABEL: Record<Priority, string> = {
  2: 'P2 · Personalised',
  3: 'P3 · Promotional',
  4: 'P4 · Fun',
}

/**
 * Step 1 of the composer. Each entry maps to a set of categories the operator
 * then narrows.
 *
 * Icons are deliberately from ONE family (lucide, the dashboard's existing set)
 * at ONE size, used as a quiet identifier at the head of each row. The previous
 * emoji treatment made the picker read as a template: emoji carry their own
 * colour, weight and cultural register, so seven of them side by side can never
 * look like a considered system.
 */
export type CampaignKind = {
  key: string
  icon: LucideIcon
  label: string
  blurb: string
  /** Shown as a small lane tag - the operator should see the cost of the lane up front. */
  lane: string
  categories: string[]
  defaultCategory: string
}

export const CAMPAIGN_KINDS: CampaignKind[] = [
  {
    key: 'offer',
    icon: BadgePercent,
    label: 'Offer',
    blurb: 'A discount or coupon. Attach the coupon so redemption and revenue are attributable.',
    lane: 'P3',
    categories: ['OFFER', 'COUPON', 'FLASH_SALE', 'FIRST_ORDER', 'LOYALTY'],
    defaultCategory: 'OFFER',
  },
  {
    key: 'reorder',
    icon: Repeat,
    label: 'Reorder',
    blurb: 'Bring a past customer back. References something they actually did, so it rides the higher lane.',
    lane: 'P2',
    categories: ['REORDER', 'REORDER_REMINDER', 'INACTIVE_USER', 'FREQUENT_CUSTOMER'],
    defaultCategory: 'REORDER',
  },
  {
    key: 'cart',
    icon: ShoppingCart,
    label: 'Cart recovery',
    blurb: 'They built a cart and stopped. One nudge, never two.',
    lane: 'P2',
    categories: ['CART_REMINDER'],
    defaultCategory: 'CART_REMINDER',
  },
  {
    key: 'personalised',
    icon: UserRound,
    label: 'Personalised',
    blurb: 'Targeted at a behaviour: high value, a service they favour, their area or the weather.',
    lane: 'P2',
    categories: ['HIGH_VALUE_CUSTOMER', 'SERVICE_RECOMMENDATION', 'LOCATION_BASED', 'WEATHER_BASED', 'COLLEGE_SEASON', 'MONSOON', 'SUMMER', 'WINTER'],
    defaultCategory: 'SERVICE_RECOMMENDATION',
  },
  {
    key: 'festival',
    icon: Sparkles,
    label: 'Festival',
    blurb: 'Seasonal campaigns. Muted separately from everyday offers.',
    lane: 'P3',
    categories: ['DIWALI', 'HOLI', 'EID', 'DURGA_PUJA', 'RAKSHA_BANDHAN', 'NEW_YEAR', 'WEDDING_SEASON'],
    defaultCategory: 'DIWALI',
  },
  {
    key: 'announcement',
    icon: Megaphone,
    label: 'Announcement',
    blurb: 'A new service, an app update, or an operational change worth telling everyone about.',
    lane: 'P3',
    categories: ['ANNOUNCEMENT', 'SERVICE_PROMOTION'],
    defaultCategory: 'ANNOUNCEMENT',
  },
  {
    key: 'meme',
    icon: MessageSquare,
    label: 'Meme',
    blurb: 'Fun, relatable, on-brand. Lowest priority, so it is the first thing dropped when someone is near their cap.',
    lane: 'P4',
    categories: ['MEME', 'FUNNY', 'TRENDING', 'BRAND_MESSAGE', 'QUIZ', 'POLL'],
    defaultCategory: 'MEME',
  },
]

/** Category → the icon used wherever a template or campaign is listed. */
export const CATEGORY_ICON: Record<string, LucideIcon> = CAMPAIGN_KINDS.reduce(
  (acc, kind) => {
    for (const category of kind.categories) acc[category] = kind.icon
    return acc
  },
  {} as Record<string, LucideIcon>
)

export const iconForCategory = (category: string): LucideIcon =>
  CATEGORY_ICON[category] ?? Megaphone

/** Deep links an operator can pick without typing. Must stay a subset of the backend allowlist. */
export const DEEPLINK_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Open the app (no specific screen)' },
  { value: 'rapidry://services', label: 'Services list' },
  // Slugs must match services.slug exactly - the app resolves the slug to a
  // service id at open time, and an unknown slug lands nowhere.
  { value: 'rapidry://services/express-steam-iron', label: 'Service · Express Steam Iron' },
  { value: 'rapidry://services/wash-and-fold', label: 'Service · Wash & Fold' },
  { value: 'rapidry://services/wash-and-iron', label: 'Service · Wash & Iron' },
  { value: 'rapidry://services/dry-cleaning', label: 'Service · Dry Cleaning' },
  { value: 'rapidry://services/shoe-care', label: 'Service · Shoe Care' },
  { value: 'rapidry://offers', label: 'Offers' },
  { value: 'rapidry://cart', label: 'Cart (resume an abandoned order)' },
  { value: 'rapidry://order/reorder', label: 'Reorder the last order' },
  { value: 'rapidry://orders', label: 'Order history' },
  { value: 'rapidry://notifications', label: 'Notification inbox' },
  { value: 'rapidry://home', label: 'Home' },
]

export const CTA_OPTIONS = [
  'Book Pickup', 'Book Now', 'Wash Now', 'Reorder', 'Continue Order',
  'Claim Offer', 'View Offer', 'Get ₹100 OFF', 'Explore Services', 'Order Now',
  'Update Now',
]

/** Plain-English suppression reasons - an operator should never have to read an enum. */
export const SUPPRESSION_LABEL: Record<string, string> = {
  freq_cap_day: 'Daily cap reached',
  freq_cap_week: 'Weekly cap reached',
  min_gap: 'Too soon after the last one',
  quiet_hours: 'Quiet hours',
  opted_out: 'Turned this category off',
  // The three reachability states. They used to be one bucket, which told an
  // operator nothing they could act on.
  //
  // no_device means "no user_devices row", which is NOT "never installed":
  // prod data (2026-08-25) showed every such customer signed up IN the app.
  // Before v1.3 the app only reported a device after notification permission
  // was granted, so decliners and dormant installs have no row. v1.3 reports
  // on every launch, so this bucket drains as installs update.
  no_device: 'App has not reported for push yet (opens on an older version)',
  permission_denied: 'Notifications turned off on their phone',
  token_invalid: 'App reinstalled or token expired',
  no_token: 'Unreachable (legacy)',
  duplicate: 'Already in this campaign',
  inactive_user: 'Account inactive',
  transactional_window: 'Just received an order update',
  campaign_paused: 'Campaign paused',
}

/** Which skip reasons the customer can be won back from, and how. */
export const SUPPRESSION_RECOVERY: Record<string, string> = {
  permission_denied: 'Recoverable: the app asks these customers to turn notifications back on.',
  token_invalid: 'Self-healing: a new token registers on their next app launch.',
  no_device: 'Not reachable by push. These customers have never installed the app.',
}

/** Lock screens truncate around here. Warn rather than block - the operator may mean it. */
export const TITLE_SOFT_LIMIT = 48
export const BODY_SOFT_LIMIT = 120

/**
 * What the preview shows before anything has been typed.
 *
 * Real Rapidry copy rather than "Notification title" / "body copy goes here":
 * a placeholder that reads like the product tells the operator what good looks
 * like, and an empty composer that already looks like a shipped notification is
 * a better brief than any instruction text.
 */
export const PLACEHOLDER_TITLE = 'Your chair is not a wardrobe'
export const PLACEHOLDER_BODY = 'Send that pile to Rapidry before the chair gives up.'
export const PLACEHOLDER_CTA = 'Book Pickup'

/**
 * What a push provider error means for the operator, and what to do about it.
 *
 * `InvalidCredentials` is the one that matters most: it is not a per-customer
 * problem at all. It means the Expo project's FCM/APNs credentials are missing,
 * invalid or revoked, so EVERY push fails until someone fixes them. Without this
 * mapping the campaign just reads "Sent: 0" and looks like a slow or broken
 * queue, which is exactly the wrong thing to go and investigate.
 */
export const PROVIDER_ERROR: Record<string, { label: string; blocking: boolean; fix?: string }> = {
  InvalidCredentials: {
    label: 'Push credentials rejected by the provider',
    blocking: true,
    fix: 'The Expo project\'s FCM (Android) and APNs (iOS) credentials are missing or revoked, so no push can be delivered to anyone. Fix with `eas credentials` in customer-app, then resume the campaign.',
  },
  MismatchSenderId: {
    label: 'Token belongs to a different Firebase sender',
    blocking: false,
    fix: 'These devices re-register on next app launch.',
  },
  DeviceNotRegistered: {
    label: 'App uninstalled or token expired',
    blocking: false,
  },
  MessageTooBig: {
    label: 'Notification payload too large',
    blocking: true,
    fix: 'Shorten the body, or drop the image.',
  },
  MessageRateExceeded: {
    label: 'Provider rate limit, retrying',
    blocking: false,
  },
}
